import { apiGet } from '../api';
import { unwrapData } from '@wapharma/shared';
import { listMentionableEntities } from './entityRegistry';

type Federated = {
  products: Array<{ id: string; name: string; slug: string; status: string; reference?: string }>;
  orders: Array<{ id: string; reference: string | null; status: string }>;
};

type MeiliHit = Record<string, unknown>;

export type MessageReferenceEntityKind = {
  refType: string;
  label: string;
  description: string;
  aliases: string[];
};

/** Derived from `entityRegistry` mention meta — keep glyph/slogan/href there. */
export const MESSAGE_REFERENCE_ENTITY_KINDS: MessageReferenceEntityKind[] = listMentionableEntities().map(
  (entry) => ({
    refType: entry.type,
    label: entry.fallbackLabel,
    description: entry.mention!.description,
    aliases: entry.mention!.aliases,
  }),
);

export type MessageReferenceSuggestion = {
  refType: string;
  refId: string;
  label: string;
};

export type MessageReferenceMenuEntry =
  | { kind: 'header'; refType: string; label: string }
  | { kind: 'entity-type'; refType: string; label: string; description: string }
  | { kind: 'result'; refType: string; refId: string; label: string };

export function getMessageReferenceEntityKind(refType: string): MessageReferenceEntityKind | null {
  return MESSAGE_REFERENCE_ENTITY_KINDS.find((kind) => kind.refType === refType) ?? null;
}

export function parseReferenceMentionQuery(query: string): { entityType: string | null; searchTerm: string } {
  const trimmed = query.trim();
  if (!trimmed) return { entityType: null, searchTerm: '' };

  const space = trimmed.indexOf(' ');
  const head = (space < 0 ? trimmed : trimmed.slice(0, space)).toLowerCase();
  const tail = space < 0 ? '' : trimmed.slice(space + 1).trim();

  for (const kind of MESSAGE_REFERENCE_ENTITY_KINDS) {
    if (
      kind.aliases.includes(head) ||
      kind.refType.toLowerCase() === head ||
      kind.label.toLowerCase() === head
    ) {
      return { entityType: kind.refType, searchTerm: tail };
    }
  }

  return { entityType: null, searchTerm: trimmed };
}

function productSearchLabel(name: string, reference?: string | null): string {
  const ref = String(reference ?? '')
    .trim()
    .split(/\s+/)[0];
  if (!ref) return name;
  return `${name} · #${ref}`;
}

function buildSuggestions(fed: Federated | null, meili: MeiliHit[], entityType: string | null): MessageReferenceSuggestion[] {
  const out: MessageReferenceSuggestion[] = [];
  const seenProducts = new Set<string>();
  const includeProduct = !entityType || entityType === 'Product';
  const includeOrder = !entityType || entityType === 'Order';

  if (includeProduct) {
    for (const h of meili) {
      const id = typeof h.id === 'string' ? h.id : String(h.id ?? '');
      if (!id || seenProducts.has(id)) continue;
      seenProducts.add(id);
      const name = typeof h.name === 'string' ? h.name : 'Produit';
      const ref = typeof h.reference === 'string' ? h.reference : null;
      out.push({
        refType: 'Product',
        refId: id,
        label: productSearchLabel(name, ref),
      });
      if (out.filter((s) => s.refType === 'Product').length >= 6) break;
    }
  }

  if (fed) {
    if (includeProduct) {
      for (const p of fed.products) {
        if (seenProducts.has(p.id)) continue;
        seenProducts.add(p.id);
        out.push({
          refType: 'Product',
          refId: p.id,
          label: productSearchLabel(p.name, p.reference ?? p.slug),
        });
        if (out.filter((s) => s.refType === 'Product').length >= 8) break;
      }
    }
    if (includeOrder) {
      for (const o of fed.orders.slice(0, 5)) {
        out.push({
          refType: 'Order',
          refId: o.id,
          label: o.reference ?? o.id.slice(0, 8),
        });
      }
    }
  }

  return out.slice(0, 12);
}

export function buildReferenceMentionMenuEntries(
  parsed: { entityType: string | null; searchTerm: string },
  suggestions: MessageReferenceSuggestion[],
): MessageReferenceMenuEntry[] {
  if (!parsed.entityType && !parsed.searchTerm) {
    return MESSAGE_REFERENCE_ENTITY_KINDS.map((kind) => ({
      kind: 'entity-type' as const,
      refType: kind.refType,
      label: kind.label,
      description: kind.description,
    }));
  }

  const filtered = parsed.entityType
    ? suggestions.filter((item) => item.refType === parsed.entityType)
    : suggestions;
  const grouped = new Map<string, MessageReferenceSuggestion[]>();
  for (const item of filtered) {
    const bucket = grouped.get(item.refType) ?? [];
    bucket.push(item);
    grouped.set(item.refType, bucket);
  }

  const typeOrder = parsed.entityType
    ? [parsed.entityType]
    : MESSAGE_REFERENCE_ENTITY_KINDS.map((kind) => kind.refType).filter((refType) => grouped.has(refType));

  const entries: MessageReferenceMenuEntry[] = [];
  for (const refType of typeOrder) {
    const kind = getMessageReferenceEntityKind(refType);
    entries.push({ kind: 'header', refType, label: kind?.label ?? refType });
    for (const item of grouped.get(refType) ?? []) {
      entries.push({
        kind: 'result',
        refType: item.refType,
        refId: item.refId,
        label: item.label,
      });
    }
  }
  return entries;
}

export function isSelectableMentionEntry(
  entry: MessageReferenceMenuEntry,
): entry is Extract<MessageReferenceMenuEntry, { kind: 'entity-type' | 'result' }> {
  return entry.kind === 'entity-type' || entry.kind === 'result';
}

export async function fetchMessageReferenceSuggestions(
  query: string,
  token: string,
  signal?: AbortSignal,
): Promise<MessageReferenceSuggestion[]> {
  const parsed = parseReferenceMentionQuery(query);
  if (!parsed.searchTerm) return [];

  const [fed, meili] = await Promise.all([
    apiGet<{ data: Federated } | Federated>(
      `/search?q=${encodeURIComponent(parsed.searchTerm)}`,
      token,
      signal,
    )
      .then((body) => unwrapData<Federated>(body))
      .catch(() => null),
    parsed.entityType && parsed.entityType !== 'Product'
      ? Promise.resolve([] as MeiliHit[])
      : apiGet<{ data: MeiliHit[] }>(`/search/products?q=${encodeURIComponent(parsed.searchTerm)}`, token, signal)
          .then((body) => {
            const d = unwrapData<MeiliHit[]>(body as { data: MeiliHit[] });
            return Array.isArray(d) ? d : [];
          })
          .catch(() => [] as MeiliHit[]),
  ]);

  return buildSuggestions(fed, meili, parsed.entityType);
}

export function detectReferenceMention(
  text: string,
  caret: number,
): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(before[at - 1]!)) return null;
  const query = before.slice(at + 1);
  if (/[\r\n]/.test(query)) return null;
  return { start: at, query };
}
